import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Globe2, Building2, Package, Truck, Users, RefreshCw, Power, Loader2, MapPin, Activity } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  live:         "bg-emerald-100 text-emerald-700 border-emerald-200",
  paused:       "bg-amber-100 text-amber-700 border-amber-200",
  provisioning: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function CityDetail() {
  const { idOrCode } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cities/${idOrCode}`);
      setCity(data);
    } catch (e) {
      toast.error("City not found");
      navigate("/dashboard/cities");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [idOrCode]);

  const toggleStatus = async () => {
    if (!city) return;
    const next = city.status === "live" ? "paused" : "live";
    if (!window.confirm(`Move ${city.name} → ${next}?`)) return;
    try {
      await api.post(`/admin/cities/${city.code}/status`, { status: next });
      toast.success(`${city.name} is now ${next}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  if (loading || !city) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" /></div>;
  }

  const k = city.kpis || {};
  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="city-detail-page">
      {/* Header */}
      <div className="animate-fadeInUp">
        <button onClick={() => navigate("/dashboard/cities")} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to all cities
        </button>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-extrabold text-slate-900 flex items-center gap-3">
              {city.code} <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase border ${STATUS_STYLE[city.status]}`}>{city.status}</span>
            </h1>
            <p className="text-base font-medium text-slate-600 mt-1 flex items-center gap-1.5">
              <Globe2 className="h-4 w-4" /> {city.name} · {city.timezone} · {city.currency}
            </p>
            {city.launched_at && <p className="text-[10px] text-slate-400 mt-0.5">Launched {new Date(city.launched_at).toLocaleDateString()}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
            <Button onClick={toggleStatus} className={`text-white gap-2 ${city.status === "live" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
                    data-testid="city-detail-toggle">
              <Power className="h-4 w-4" /> {city.status === "live" ? "Pause city" : "Resume city"}
            </Button>
          </div>
        </div>
      </div>

      {/* Feature flags */}
      {city.feature_flags && Object.keys(city.feature_flags).length > 0 && (
        <div className="flex flex-wrap gap-2 animate-fadeInUp" data-testid="city-feature-flags">
          {Object.entries(city.feature_flags).map(([k, v]) => (
            <span key={k} className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase border ${v ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
              {v ? "✓ " : "✗ "} {k.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-fadeInUp" data-testid="city-kpis">
        <KpiCard icon={Building2} label="Hubs" value={k.hub_count} color="text-violet-700" />
        <KpiCard icon={Package} label="Orders (7d)" value={k.orders_7d} sub={`${k.orders_24h} in last 24h`} color="text-[#FF6B00]" />
        <KpiCard icon={Activity} label="Active orders" value={k.active_orders} color="text-blue-700" />
        <KpiCard icon={Truck} label="Delivered (7d)" value={k.delivered_7d} color="text-emerald-700" />
        <KpiCard icon={Users} label="Active agents" value={k.active_agents} color="text-slate-700" />
        <KpiCard icon={Truck} label="Linehaul (7d)" value={k.linehaul_batches_7d} sub="inter-city batches" color="text-pink-700" />
        <KpiCard icon={Package} label="Total orders" value={k.orders_total} sub="all-time" color="text-slate-700" />
      </div>

      {/* Hubs list */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="city-hubs-list">
        <div className="p-5 border-b border-slate-100 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-slate-500" />
          <h2 className="font-['Outfit'] text-base font-semibold text-slate-900">Hubs in {city.name}</h2>
          <span className="text-xs text-slate-400">· {city.hubs?.length || 0}</span>
        </div>
        {!city.hubs?.length ? (
          <div className="p-10 text-center text-sm text-slate-500">No hubs yet. <a href="/dashboard/hubs" className="text-[#FF6B00] hover:underline">Add one →</a></div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {city.hubs.map((h) => (
              <li key={h.hub_id} className="p-4 sm:px-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{h.name}</p>
                    <p className="text-[10px] text-slate-400 uppercase font-bold tracking-widest">{h.hub_type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${h.active !== false ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {h.active !== false ? "Active" : "Inactive"}
                  </span>
                  {h.lat && h.lng && (
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{h.lat}, {h.lng}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-[10px] text-slate-400 text-center" data-testid="city-disclaimer">
        Per-city KPIs computed from real data — no forecasting. Forecasting unlocks at 1,500+ delivered orders platform-wide.
      </p>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color = "text-slate-700" }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4">
      <div className="flex items-center gap-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      </div>
      <p className={`mt-1.5 text-2xl font-extrabold font-['Outfit'] ${color}`}>{value ?? 0}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}
